import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { getLoungeProfile, saveLoungeProfile, trackLoungeEvent } from "@/lib/loungeTrack";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Info, Send } from "lucide-react";

// Form riutilizzabile per le richieste pubbliche della Lounge.
// Invia SEMPRE il campo `ref` (attribuzione) insieme al payload.
export function LoungeForm({ endpoint, fields, refValue, extra = {}, note, title, subtitle, testidPrefix, copy }) {
  const c = copy;
  const empty = fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {});
  const [values, setValues] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name || !values.name.trim()) return toast.error(c.f_name);
    if (!values.email || !values.email.includes("@")) return toast.error(c.f_email);
    setSending(true);
    try {
      await api.post(endpoint, { ...values, ...extra, ref: refValue || "" });
      if ((extra?.source_page || "") === "ambassador" || /ambassador|kit/.test(endpoint)) {
        if (!getLoungeProfile("ambassador")?.email) saveLoungeProfile("ambassador", { name: values.name, email: values.email, social: values.social || "" });
        trackLoungeEvent({ area: "ambassador", type: "access", item: { kind: "form", title: title || endpoint } });
      }
      toast.success(c.success);
      setValues(empty);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-6 sm:p-8"
      data-testid={`${testidPrefix}-card`}
    >
      <h3 className="text-2xl font-bold text-foreground">{title}</h3>
      {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}

      <form onSubmit={submit} className="mt-6 space-y-4" data-testid={`${testidPrefix}-form`}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields
            .filter((f) => f.type !== "textarea")
            .map((f) => (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="mb-1.5 block text-xs text-muted-foreground">
                  {c[f.labelKey]} {f.required && <span className="text-brand">*</span>}
                </Label>
                <Input
                  type={f.type || "text"}
                  value={values[f.name]}
                  onChange={set(f.name)}
                  placeholder={c[f.labelKey]}
                  data-testid={`${testidPrefix}-field-${f.name}`}
                />
              </div>
            ))}
        </div>
        {fields
          .filter((f) => f.type === "textarea")
          .map((f) => (
            <div key={f.name}>
              <Label className="mb-1.5 block text-xs text-muted-foreground">{c[f.labelKey]}</Label>
              <Textarea
                rows={4}
                value={values[f.name]}
                onChange={set(f.name)}
                placeholder={c[f.labelKey]}
                data-testid={`${testidPrefix}-field-${f.name}`}
              />
            </div>
          ))}

        {note && (
          <div
            className="flex gap-2.5 rounded-xl border border-brand/30 bg-brand/10 p-3.5 text-xs leading-relaxed text-muted-foreground"
            data-testid={`${testidPrefix}-note`}
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span>{note}</span>
          </div>
        )}

        {refValue && (
          <p className="text-xs text-muted-foreground" data-testid={`${testidPrefix}-ref`}>
            {c.ref_note} <span className="font-semibold text-foreground">{refValue}</span>
          </p>
        )}

        <Button
          type="submit"
          disabled={sending}
          className="rounded-full bg-primary px-7 font-semibold text-primary-foreground hover:bg-primary/90"
          data-testid={`${testidPrefix}-submit`}
        >
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          {sending ? c.sending : c.submit}
        </Button>
      </form>
    </div>
  );
}
